import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  Modal,
  Form,
  message,
  Tooltip,
  Progress,
  Badge,
  Dropdown,
  Menu,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  FilterOutlined,
  ExportOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  FileTextOutlined,
  DollarOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import type { PurchaseRequisition, RequisitionStatus, Priority } from '../../types';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface PurchaseRequisitionsProps {
  requisitions: PurchaseRequisition[];
  onRequisitionCreate: () => void;
  onRequisitionUpdate: (id: string, data: any) => void;
  onRequisitionDelete: (id: string) => void;
}

export const PurchaseRequisitions: React.FC<PurchaseRequisitionsProps> = ({
  requisitions,
  onRequisitionCreate,
  onRequisitionUpdate,
  onRequisitionDelete,
}) => {
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<RequisitionStatus | undefined>();
  const [priorityFilter, setPriorityFilter] = useState<Priority | undefined>();
  const [departmentFilter, setDepartmentFilter] = useState<string | undefined>();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // Mock data for demonstration
  const mockRequisitions: PurchaseRequisition[] = [
    {
      id: '1',
      requisitionNumber: 'PR-2024-001',
      title: 'Office Supplies Q1 2024',
      description: 'Monthly office supplies including stationery, paper, and consumables',
      department: 'Administration',
      requestedBy: 'John Doe',
      requestedDate: new Date('2024-01-15'),
      requiredDate: new Date('2024-02-01'),
      priority: 'normal',
      status: 'pending_approval',
      totalAmount: 5500,
      currency: 'USD',
      items: [
        {
          id: '1',
          lineNumber: 1,
          productName: 'A4 Paper',
          description: 'White A4 copy paper, 80gsm',
          category: 'office_supplies',
          quantity: 50,
          unitOfMeasure: 'ream',
          estimatedUnitPrice: 8.50,
          estimatedTotalPrice: 425,
          deliveryDate: new Date('2024-02-01'),
          status: 'pending',
        },
        {
          id: '2',
          lineNumber: 2,
          productName: 'Ballpoint Pens',
          description: 'Blue ink ballpoint pens',
          category: 'office_supplies',
          quantity: 100,
          unitOfMeasure: 'piece',
          estimatedUnitPrice: 1.20,
          estimatedTotalPrice: 120,
          deliveryDate: new Date('2024-02-01'),
          status: 'pending',
        },
      ],
      approvalFlow: {} as any,
      approvals: [],
      attachments: [],
      createdAt: new Date('2024-01-15'),
      updatedAt: new Date('2024-01-15'),
    },
    {
      id: '2',
      requisitionNumber: 'PR-2024-002',
      title: 'IT Equipment Upgrade',
      description: 'Laptop and monitor upgrades for development team',
      department: 'IT',
      requestedBy: 'Jane Smith',
      requestedDate: new Date('2024-01-20'),
      requiredDate: new Date('2024-02-15'),
      priority: 'high',
      status: 'approved',
      totalAmount: 25000,
      currency: 'USD',
      items: [],
      approvalFlow: {} as any,
      approvals: [],
      attachments: [],
      createdAt: new Date('2024-01-20'),
      updatedAt: new Date('2024-01-22'),
    },
    {
      id: '3',
      requisitionNumber: 'PR-2024-003',
      title: 'Marketing Materials',
      description: 'Brochures and promotional materials for trade show',
      department: 'Marketing',
      requestedBy: 'Mike Johnson',
      requestedDate: new Date('2024-01-18'),
      requiredDate: new Date('2024-02-10'),
      priority: 'urgent',
      status: 'rejected',
      totalAmount: 8500,
      currency: 'USD',
      items: [],
      approvalFlow: {} as any,
      approvals: [],
      attachments: [],
      createdAt: new Date('2024-01-18'),
      updatedAt: new Date('2024-01-19'),
    },
  ];

  const [filteredData, setFilteredData] = useState(mockRequisitions);

  useEffect(() => {
    let filtered = mockRequisitions;

    if (searchText) {
      filtered = filtered.filter(req =>
        req.requisitionNumber.toLowerCase().includes(searchText.toLowerCase()) ||
        req.title.toLowerCase().includes(searchText.toLowerCase()) ||
        req.requestedBy.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(req => req.status === statusFilter);
    }

    if (priorityFilter) {
      filtered = filtered.filter(req => req.priority === priorityFilter);
    }

    if (departmentFilter) {
      filtered = filtered.filter(req => req.department === departmentFilter);
    }

    setFilteredData(filtered);
  }, [searchText, statusFilter, priorityFilter, departmentFilter]);

  const getStatusColor = (status: RequisitionStatus): string => {
    switch (status) {
      case 'draft': return 'default';
      case 'submitted': return 'processing';
      case 'pending_approval': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'cancelled': return 'default';
      case 'converted_to_po': return 'cyan';
      case 'partially_converted': return 'blue';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: Priority): string => {
    switch (priority) {
      case 'low': return 'green';
      case 'normal': return 'blue';
      case 'high': return 'orange';
      case 'urgent': return 'red';
      case 'critical': return 'purple';
      default: return 'blue';
    }
  };

  const getStatusIcon = (status: RequisitionStatus) => {
    switch (status) {
      case 'approved': return <CheckCircleOutlined />;
      case 'pending_approval': return <ClockCircleOutlined />;
      case 'rejected': return <StopOutlined />;
      case 'cancelled': return <ExclamationCircleOutlined />;
      default: return <FileTextOutlined />;
    }
  };

  const handleAction = (record: PurchaseRequisition, action: string) => {
    switch (action) {
      case 'view':
        message.info(`Viewing requisition ${record.requisitionNumber}`);
        break;
      case 'edit':
        onRequisitionUpdate(record.id, record);
        break;
      case 'delete':
        Modal.confirm({
          title: 'Delete Requisition',
          content: `Are you sure you want to delete requisition ${record.requisitionNumber}?`,
          onOk: () => onRequisitionDelete(record.id),
        });
        break;
      case 'approve':
        message.success(`Requisition ${record.requisitionNumber} approved`);
        break;
      case 'reject':
        message.warning(`Requisition ${record.requisitionNumber} rejected`);
        break;
    }
  };

  const actionMenu = (record: PurchaseRequisition) => (
    <Menu>
      <Menu.Item key="view" icon={<EyeOutlined />} onClick={() => handleAction(record, 'view')}>
        View Details
      </Menu.Item>
      <Menu.Item key="edit" icon={<EditOutlined />} onClick={() => handleAction(record, 'edit')}>
        Edit
      </Menu.Item>
      {record.status === 'pending_approval' && (
        <>
          <Menu.Item key="approve" icon={<CheckCircleOutlined />} onClick={() => handleAction(record, 'approve')}>
            Approve
          </Menu.Item>
          <Menu.Item key="reject" icon={<StopOutlined />} onClick={() => handleAction(record, 'reject')}>
            Reject
          </Menu.Item>
        </>
      )}
      <Menu.Divider />
      <Menu.Item key="delete" danger icon={<DeleteOutlined />} onClick={() => handleAction(record, 'delete')}>
        Delete
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: 'Requisition Number',
      dataIndex: 'requisitionNumber',
      key: 'requisitionNumber',
      width: 150,
      fixed: 'left' as const,
      render: (text: string, record: PurchaseRequisition) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ color: '#1890ff', cursor: 'pointer' }} onClick={() => handleAction(record, 'view')}>
            {text}
          </Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.createdAt.toLocaleDateString()}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Title & Description',
      dataIndex: 'title',
      key: 'title',
      width: 250,
      render: (text: string, record: PurchaseRequisition) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <Text type="secondary" ellipsis style={{ fontSize: '12px', maxWidth: '200px' }}>
            {record.description}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      width: 120,
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: 'Requested By',
      dataIndex: 'requestedBy',
      key: 'requestedBy',
      width: 130,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority: Priority) => (
        <Tag color={getPriorityColor(priority)} style={{ textTransform: 'capitalize' }}>
          {priority}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: RequisitionStatus, record: PurchaseRequisition) => (
        <Badge 
          status={getStatusColor(status) as any} 
          text={
            <Space>
              {getStatusIcon(status)}
              <span style={{ textTransform: 'capitalize' }}>
                {status.replace('_', ' ')}
              </span>
            </Space>
          } 
        />
      ),
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 120,
      render: (amount: number, record: PurchaseRequisition) => (
        <Text strong style={{ color: '#1890ff' }}>
          {record.currency} {amount.toLocaleString()}
        </Text>
      ),
    },
    {
      title: 'Required Date',
      dataIndex: 'requiredDate',
      key: 'requiredDate',
      width: 120,
      render: (date: Date) => (
        <Text style={{ color: new Date(date) < new Date() ? '#ff4d4f' : '#000' }}>
          {date.toLocaleDateString()}
        </Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 60,
      fixed: 'right' as const,
      render: (_, record: PurchaseRequisition) => (
        <Dropdown overlay={actionMenu(record)} trigger={['click']} placement="bottomRight">
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(selectedRowKeys);
    },
  };

  const departments = ['Administration', 'IT', 'Marketing', 'Sales', 'Finance', 'HR', 'Operations'];

  return (
    <div className="purchase-requisitions">
      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Requisitions"
              value={filteredData.length}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Pending Approval"
              value={filteredData.filter(r => r.status === 'pending_approval').length}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Approved"
              value={filteredData.filter(r => r.status === 'approved').length}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Value"
              value={filteredData.reduce((sum, r) => sum + r.totalAmount, 0)}
              prefix={<DollarOutlined />}
              precision={0}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Table Card */}
      <Card
        title={<Title level={4}>Purchase Requisitions</Title>}
        extra={
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={onRequisitionCreate}>
              New Requisition
            </Button>
            <Button icon={<ExportOutlined />}>Export</Button>
          </Space>
        }
      >
        {/* Filters */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Input
              placeholder="Search requisitions..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="draft">Draft</Option>
              <Option value="submitted">Submitted</Option>
              <Option value="pending_approval">Pending Approval</Option>
              <Option value="approved">Approved</Option>
              <Option value="rejected">Rejected</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Priority"
              value={priorityFilter}
              onChange={setPriorityFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="low">Low</Option>
              <Option value="normal">Normal</Option>
              <Option value="high">High</Option>
              <Option value="urgent">Urgent</Option>
              <Option value="critical">Critical</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Department"
              value={departmentFilter}
              onChange={setDepartmentFilter}
              allowClear
              style={{ width: '100%' }}
            >
              {departments.map(dept => (
                <Option key={dept} value={dept}>{dept}</Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <RangePicker style={{ width: '100%' }} />
          </Col>
        </Row>

        {/* Bulk Actions */}
        {selectedRowKeys.length > 0 && (
          <div style={{ marginBottom: 16, padding: 8, background: '#f0f2f5', borderRadius: 4 }}>
            <Space>
              <Text>{selectedRowKeys.length} items selected</Text>
              <Button size="small">Bulk Approve</Button>
              <Button size="small">Bulk Reject</Button>
              <Button size="small" danger>Bulk Delete</Button>
            </Space>
          </div>
        )}

        {/* Table */}
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          rowSelection={rowSelection}
          scroll={{ x: 1200 }}
          pagination={{
            total: filteredData.length,
            //pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: [10, 20, 50, 100, 500],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
        />
      </Card>
    </div>
  );
}; 